import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ColumnOptions, Ders, Result, RunState } from '@model';
import { ResultService } from '@service';

type ResultTableRow = Result & { rte?: number | null };

@Component({
  selector: 'optima-result-table',
  templateUrl: './result-table.component.html',
  styleUrls: ['./result-table.component.scss'],
})
export class ResultTableComponent implements OnChanges, AfterViewInit {
  @Input() results: Result[];
  @Input() ders: Ders[];
  @Input() selectedIdx: number;
  @Output() copiedDataEmitter = new EventEmitter<string>();
  @ViewChild(MatSort) sort: MatSort;
  dataSource: MatTableDataSource<ResultTableRow>;
  columns: ColumnOptions[];
  displayedColumns: string[];
  copiedData: string;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly resultService: ResultService,
    private readonly translateService: TranslateService,
  ) {
    this.columns = [
      new ColumnOptions('runState', 'scenario.results.run_state', false),
      new ColumnOptions('idx', 'scenario.results.config_number', false),
      new ColumnOptions(
        'solarPvNameplate',
        'scenario.results.solar_pv_nameplate',
        true,
      ),
      new ColumnOptions('storagePower', 'scenario.results.storage_power', true),
      new ColumnOptions(
        'storageCapacity',
        'scenario.results.storage_capacity',
        true,
      ),
      new ColumnOptions('rte', 'scenario.results.rte', true),
      new ColumnOptions(
        'solarSavings',
        'scenario.results.solar_savings',
        false,
      ),
      new ColumnOptions(
        'storageSavings',
        'scenario.results.storage_savings',
        false,
      ),
      new ColumnOptions(
        'tariffSwitchSavings',
        'scenario.results.tariff_switch_savings',
        false,
      ),
      new ColumnOptions(
        'gridServiceRevenues',
        'scenario.results.grid_services_revenues',
        false,
      ),
      new ColumnOptions(
        'totalSavingsAndRevenues',
        'scenario.results.total_savings_and_revenues',
        false,
      ),
      new ColumnOptions(
        'storageCycles',
        'scenario.results.storage_cycles',
        false,
      ),
      new ColumnOptions(
        'storageSavingsPerKwh',
        'scenario.results.total_storage_savings',
        false,
      ),
    ];
    this.displayedColumns = Array.from(this.columns.map(k => k.id));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {
    this.initDataSource(this.results);
    this.selectedIdx = this.selectedIdx ?? this.dataSource.data[0].idx;
    void this.selectResult();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  async toggleSelectedRow(result: Result): Promise<void> {
    if (result.runState === RunState.STARTED) {
      return;
    }
    this.selectedIdx = result.idx;
    if (this.selectedIdx !== null && this.selectedIdx !== undefined) {
      await this.selectResult();
    }
  }

  initDataSource(data: Result[]): void {
    const rows = data?.map(result => ({
      ...result,
      rte: this.getResultRte(result),
    }));

    this.dataSource = new MatTableDataSource(rows);
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }

    this.copiedData = [
      Array.from(this.columns.map(column => column.translatedKey)).map(key =>
        this.translateService.instant(key),
      ),
      ...this.dataSource.data.map(item =>
        Array.from(this.columns.map(column => column.id)).map(id => item[id]),
      ),
    ]
      .map(data => data.join(','))
      .join('\n');

    this.copiedDataEmitter.emit(this.copiedData);
  }

  private async selectResult(): Promise<void> {
    const selectedResult = this.dataSource.data.find(
      c => c.idx === this.selectedIdx,
    );
    if (selectedResult && selectedResult.runState !== RunState.STARTED) {
      await this.appendIdxToQueryParam(selectedResult.idx);
      this.resultService.selectResult(selectedResult.adjustedLoadProfileId);
    }
  }

  private async appendIdxToQueryParam(idx: number): Promise<void> {
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        idx: idx,
      },
      queryParamsHandling: 'merge',
    });
  }

  getTranslateKey(columnId: string): string {
    return this.columns.find(k => k.id === columnId).translatedKey;
  }

  getPrintInfo(columnId: string): boolean {
    return this.columns.find(k => k.id === columnId).showUnitInBody;
  }

  hasColumnValues(column: string): boolean {
    return this.dataSource.data.some(k => Boolean(k[column]));
  }

  private getResultRte(result: Result): number | null {
    const indexedDer = this.ders?.[result.idx - 1];
    if (this.matchesResultConfig(indexedDer, result)) {
      return this.normalizeRte(indexedDer.rte);
    }

    const matchedDers = (this.ders ?? []).filter(der =>
      this.matchesResultConfig(der, result),
    );
    if (matchedDers.length === 1) {
      return this.normalizeRte(matchedDers[0].rte);
    }

    return null;
  }

  private matchesResultConfig(der: Ders | undefined, result: Result): boolean {
    if (!der) {
      return false;
    }

    return (
      Math.abs(der.totalKw - result.storagePower) < 0.001 &&
      Math.abs(der.totalKwh - result.storageCapacity) < 0.001
    );
  }

  private normalizeRte(rte?: number): number | null {
    if (typeof rte !== 'number') {
      return null;
    }

    return rte <= 1 ? rte * 100 : rte;
  }
}
